import { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import type { Gate, PmAccessLogFilters, PmVisitStatus } from '@/types';

const STATUS_OPTIONS: { label: string; value: PmVisitStatus }[] = [
  { label: 'On Property', value: 'granted' },
  { label: 'Checked Out', value: 'checked_out' },
  { label: 'Denied', value: 'denied' },
  { label: 'Expired', value: 'expired' },
];

interface AccessLogFiltersProps {
  gates: Gate[];
  filters: PmAccessLogFilters;
  onChange: (filters: PmAccessLogFilters) => void;
  onReset: () => void;
}

export function AccessLogFilters({ gates, filters, onChange, onReset }: AccessLogFiltersProps) {
  const [nameSearch, setNameSearch] = useState(filters.visitorName ?? '');

  const activeFilterCount = [
    filters.gateIds?.length,
    filters.statuses?.length,
    filters.visitorName,
    filters.dateFrom,
    filters.dateTo,
  ].filter(Boolean).length;

  const buildFilters = (updates: Record<string, unknown>): PmAccessLogFilters => {
    const result: Record<string, unknown> = {};

    if ('dateFrom' in updates) result.dateFrom = updates.dateFrom as string;
    else if (filters.dateFrom) result.dateFrom = filters.dateFrom;

    if ('dateTo' in updates) result.dateTo = updates.dateTo as string;
    else if (filters.dateTo) result.dateTo = filters.dateTo;

    if ('gateIds' in updates) result.gateIds = updates.gateIds as string[];
    else if (filters.gateIds) result.gateIds = filters.gateIds;

    if ('statuses' in updates) result.statuses = updates.statuses as PmVisitStatus[];
    else if (filters.statuses) result.statuses = filters.statuses;

    if ('invitationTypes' in updates) result.invitationTypes = updates.invitationTypes;
    else if (filters.invitationTypes) result.invitationTypes = filters.invitationTypes;

    if ('visitorName' in updates) result.visitorName = updates.visitorName as string;
    else if (filters.visitorName) result.visitorName = filters.visitorName;

    if ('unit' in updates) result.unit = updates.unit as string;
    else if (filters.unit) result.unit = filters.unit;

    if ('guardName' in updates) result.guardName = updates.guardName as string;
    else if (filters.guardName) result.guardName = filters.guardName;

    if ('cursor' in updates) result.cursor = updates.cursor as string;
    else if (filters.cursor) result.cursor = filters.cursor;

    if ('limit' in updates) result.limit = updates.limit as number;
    else if (filters.limit) result.limit = filters.limit;

    return result as PmAccessLogFilters;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Visitor name search */}
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-8 w-48"
          placeholder="Visitor name…"
          value={nameSearch}
          onChange={(e) => setNameSearch(e.target.value)}
          onBlur={() => onChange(buildFilters({ visitorName: nameSearch || undefined }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onChange(buildFilters({ visitorName: nameSearch || undefined }));
          }}
        />
      </div>

      {/* Gate filter */}
      <Select
        value={filters.gateIds?.[0] ?? '__all__'}
        onValueChange={(v) =>
          onChange(buildFilters({ gateIds: v !== '__all__' ? [v] : undefined }))
        }
      >
        <SelectTrigger className="w-36">
          <SelectValue placeholder="All gates" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All gates</SelectItem>
          {gates.map((g) => (
            <SelectItem key={g.id} value={g.id}>
              {g.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Status filter */}
      <Select
        value={filters.statuses?.[0] ?? '__all__'}
        onValueChange={(v) =>
          onChange(
            buildFilters({
              statuses: v !== '__all__' ? [v as PmVisitStatus] : undefined,
            }),
          )
        }
      >
        <SelectTrigger className="w-36">
          <SelectValue placeholder="All statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All statuses</SelectItem>
          {STATUS_OPTIONS.map((s) => (
            <SelectItem key={s.value} value={s.value}>
              {s.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Date range — simple date inputs */}
      <Input
        type="date"
        className="w-36"
        value={filters.dateFrom ?? ''}
        onChange={(e) => onChange(buildFilters({ dateFrom: e.target.value || undefined }))}
      />
      <Input
        type="date"
        className="w-36"
        value={filters.dateTo ?? ''}
        onChange={(e) => onChange(buildFilters({ dateTo: e.target.value || undefined }))}
      />

      {activeFilterCount > 0 && (
        <Button variant="ghost" size="sm" onClick={onReset}>
          Clear
          <Badge variant="secondary" className="ml-1">
            {activeFilterCount}
          </Badge>
        </Button>
      )}
    </div>
  );
}
